import { OpenImgContextProvider } from "openimg/react";
import { useEffect } from "react";
import {
  data,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useMatches,
} from "react-router";
import { HoneypotProvider } from "remix-utils/honeypot/react";
import { type Route } from "./+types/root.ts";
import appleTouchIconAssetUrl from "./assets/favicons/apple-touch-icon.png";
import faviconAssetUrl from "./assets/favicons/favicon.ico";
import faviconSvgUrl from "./assets/favicons/favicon.svg";
import { GeneralErrorBoundary } from "./components/error-boundary.tsx";
import { Navbar } from "./components/navbar.tsx";
import { EpicProgress } from "./components/progress-bar.tsx";
import { SearchBar } from "./components/search-bar.tsx";
import { useToast } from "./components/toaster.tsx";
import { href as iconsHref } from "./components/ui/icon.tsx";
import { EpicToaster } from "./components/ui/sonner.tsx";
import {
  CookieConsentBanner,
  useOptimisticCookieConsent,
} from "./routes/resources+/cookie-consent.tsx";
import {
  useOptionalTheme,
  useTheme,
} from "./routes/resources+/theme-switch.tsx";
import tailwindStyleSheetUrl from "./styles/tailwind.css?url";
import { getUserId } from "./utils/auth.server.ts";
import { ClientHintCheck, getHints } from "./utils/client-hints.tsx";
import { getCookieConsent } from "./utils/cookie-consent.server.ts";
import { prisma } from "./utils/db.server.ts";
import { getEnv } from "./utils/env.server.ts";
import * as gtag from "./utils/gtags.client.ts";
import { pipeHeaders } from "./utils/headers.server.ts";
import { honeypot } from "./utils/honeypot.server.ts";
import { combineHeaders, getDomainUrl, getImgSrc } from "./utils/misc.tsx";
import { useNonce } from "./utils/nonce-provider.ts";
import { type Theme, getTheme } from "./utils/theme.server.ts";
import { makeTimings, time } from "./utils/timing.server.ts";
import { getToast } from "./utils/toast.server.ts";

export const links: Route.LinksFunction = () => {
  return [
    // Preload svg sprite as a resource to avoid render blocking
    { rel: "preload", href: iconsHref, as: "image" },
    { rel: "icon", href: faviconAssetUrl, sizes: "32x32" },
    { rel: "icon", type: "image/svg+xml", href: faviconSvgUrl },
    { rel: "apple-touch-icon", href: appleTouchIconAssetUrl },
    {
      rel: "manifest",
      href: "/site.webmanifest",
      crossOrigin: "use-credentials",
    } as const,
    { rel: "stylesheet", href: tailwindStyleSheetUrl },
  ].filter(Boolean);
};

export const meta: Route.MetaFunction = () => {
  return [
    { charSet: "utf-8" },
    { name: "viewport", content: "width=device-width,initial-scale=1" },
    { title: "Shona Slang Words & Meanings | shonaslang Dictionary" },
    {
      name: "description",
      content:
        "Shona slang dictionary of Zimbabwean street lingo, ghetto speak and ghetto yout words. Search meanings, usage and examples, or add your own.",
    },
    {
      name: "keywords",
      content:
        "shona slang, zimbabwe slang, zim slang, zimbo slang, zim street language, street lingo, ghetto speak, ghetto talk, ghetto yout, ghetto youth, zimdancehall slang, zimdancehall lyrics meaning, harare slang, shona, ghetto sasi, masasi emu ghetto, sasi remughetto, kuvhura hombe meaning, urban shona dictionary",
    },
    { name: "robots", content: "index,follow" },
    { name: "author", content: "shonaslang" },

    { property: "og:site_name", content: "shonaslang" },
    { property: "og:type", content: "website" },
    {
      property: "og:title",
      content: "Shona Slang Words & Meanings | shonaslang Dictionary",
    },
    {
      property: "og:description",
      content:
        "Shona slang dictionary of Zimbabwean street lingo, ghetto speak and ghetto yout words. Search meanings, usage and examples, or add your own.",
    },
    { property: "og:url", content: "https://shonaslang.com" },
    { property: "og:image", content: "https://shonaslang.com/og-image.png" },
    { property: "og:locale", content: "en_GB" },

    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "shonaslang Dictionary" },
    {
      name: "twitter:description",
      content:
        "Zimbabwean street lingo and ghetto speak, translated to English.",
    },
    { name: "twitter:image", content: "https://shonaslang.com/og-image.png" },
  ];
};

export async function loader({ request }: Route.LoaderArgs) {
  const timings = makeTimings("root loader");
  const userId = await time(() => getUserId(request), {
    timings,
    type: "getUserId",
    desc: "getUserId in root",
  });

  const user = userId
    ? await time(
        () =>
          prisma.user.findUnique({
            select: {
              id: true,
              displayName: true,
              username: true,
              image: { select: { objectKey: true } },
              roles: {
                select: {
                  name: true,
                  permissions: {
                    select: { entity: true, action: true, access: true },
                  },
                },
              },
            },
            where: { id: userId },
          }),
        { timings, type: "find user", desc: "find user in root" },
      )
    : null;
  if (userId && !user) {
    console.warn(`⚠️ User ${userId} has session but not found in DB`);
    // something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
    // await logout({ request, redirectTo: "/" });
  }
  // Get unread notification count for admin users
  const isAdmin = user?.roles.some((r) => r.name === "admin");
  const unreadNotificationCount =
    isAdmin && userId
      ? await prisma.notification.count({
          where: { userId, read: false },
        })
      : 0;

  const { toast, headers: toastHeaders } = await getToast(request);
  const honeyProps = await honeypot.getInputProps();
  const cookieConsent = getCookieConsent(request);

  return data(
    {
      user,
      unreadNotificationCount,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request),
        },
      },
      ENV: getEnv(),
      toast,
      honeyProps,
      cookieConsent,
    },
    {
      headers: combineHeaders(
        { "Server-Timing": timings.toString() },
        toastHeaders,
      ),
    },
  );
}

export const headers: Route.HeadersFunction = pipeHeaders;

function Document({
  children,
  nonce,
  theme = "light",
  env = {},
  cookieConsent,
  canonicalUrl,
}: {
  children: React.ReactNode;
  nonce: string;
  theme?: Theme;
  env?: Record<string, string | undefined>;
  cookieConsent?: string | null;
  canonicalUrl?: string;
}) {
  const location = useLocation();
  const allowIndexing = ENV.ALLOW_INDEXING !== "false";
  const optimisticConsent = useOptimisticCookieConsent();
  const hasAnalyticsConsent =
    (optimisticConsent ?? cookieConsent) === "accepted";
  const gaTrackingId = env.GA_TRACKING_ID;

  useEffect(() => {
    if (hasAnalyticsConsent && gaTrackingId?.length) {
      gtag.pageview(location.pathname + location.search, gaTrackingId);
    }
  }, [location, hasAnalyticsConsent, gaTrackingId]);

  return (
    <html lang="en" className={`${theme} h-full overflow-x-hidden`}>
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta
          name="theme-color"
          content={theme === "dark" ? "#020817" : "#FFFFFF"}
        />
        {allowIndexing ? null : (
          <meta name="robots" content="noindex, nofollow" />
        )}
        {canonicalUrl ? (
          <link rel="canonical" href={canonicalUrl} />
        ) : null}
        <Links />
      </head>
      <body className="bg-gray-50 dark:bg-background text-foreground">
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)};`,
          }}
        />
        {hasAnalyticsConsent && gaTrackingId ? (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`}
            />
            <script
              nonce={nonce}
              dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${gaTrackingId}', {
                    page_path: window.location.pathname,
                  });
                `,
              }}
            />
          </>
        ) : null}
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useLoaderData<typeof loader | null>();
  const nonce = useNonce();
  const theme = useOptionalTheme();
  const canonicalUrl = data?.requestInfo
    ? `${data.requestInfo.origin}${data.requestInfo.path}`
    : undefined;
  return (
    <Document
      nonce={nonce}
      theme={theme}
      env={data?.ENV}
      cookieConsent={data?.cookieConsent}
      canonicalUrl={canonicalUrl}
    >
      {children}
    </Document>
  );
}

function App() {
  const data = useLoaderData<typeof loader>();
  const theme = useTheme();
  const matches = useMatches();
  const isOnSearchPage = matches.find((m) => m.id === "routes/users+/index");
  const searchBar = isOnSearchPage ? null : (
    <SearchBar
      status="idle"
      placeholder="Search words..."
      searchParamName="q"
    />
  );
  useToast(data.toast);

  return (
    <OpenImgContextProvider
      optimizerEndpoint="/resources/images"
      getSrc={getImgSrc}
    >
      <div className="flex min-h-screen flex-col">
        <Navbar
          searchBar={searchBar}
          userPreference={data.requestInfo.userPrefs.theme}
        />

        <main className="flex-1">
          <Outlet />
        </main>

        <footer className="bg-white dark:bg-card border-t border-gray-200 dark:border-border">
          <div className="container py-6">
            <div className="flex flex-col items-center justify-center space-y-2 text-center">
              <p className="text-sm text-muted-foreground">
                shonaslang - preserving contemporary Shona slang
              </p>
              <p className="text-xs text-muted-foreground">
                Built with ❤️ for the Zimbabwean community
              </p>
            </div>
          </div>
        </footer>
      </div>
      <CookieConsentBanner cookieConsent={data.cookieConsent} />
      <EpicToaster closeButton position="top-center" theme={theme} />
      <EpicProgress />
    </OpenImgContextProvider>
  );
}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>();
  return (
    <HoneypotProvider {...data.honeyProps}>
      <App />
    </HoneypotProvider>
  );
}

export default AppWithProviders;

export const ErrorBoundary = GeneralErrorBoundary;
